import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, Avatar, TextField, FormControl, InputLabel, Select, MenuItem, Badge, Alert, LinearProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as AccountBalanceIcon,
  LocalAtm as LocalAtmIcon,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { paymentsService } from '@services/paymentsService';
import { clinicPaymentsService } from '@services/clinicPaymentsService';
import { invoicesService } from '@services/invoicesService';
import { formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

function invoiceTabBucket(status: string): 'draft' | 'open' | 'paid' | 'cancelled' {
  const s = String(status || '').toLowerCase();
  if (s === 'draft') return 'draft';
  if (s === 'paid') return 'paid';
  if (['void', 'cancelled', 'canceled'].includes(s)) return 'cancelled';
  return 'open';
}

function paymentRowAmount(pay: Record<string, unknown>): number {
  const cents = pay.amount_cents as number | undefined;
  const amt = pay.amount as number | undefined;
  if (typeof cents === 'number') return cents / 100;
  if (typeof amt === 'number') return amt;
  return 0;
}

function isStripeChargeSuccessful(st: string | null | undefined): boolean {
  const s = String(st || '').toLowerCase();
  return s === 'completed' || s === 'succeeded' || s === 'partially_refunded';
}

function canStripeRetry(st: string | null | undefined): boolean {
  const s = String(st || '').toLowerCase();
  return ['failed', 'cancelled', 'canceled', 'requires_action', 'pending'].includes(s);
}

function normalizePaymentRow(pay: Record<string, unknown>, _clinicId: string): Payment {
  const invId = String(pay.invoice_id ?? pay.invoiceId ?? '');
  const amt = paymentRowAmount(pay);
  const st = String(pay.status ?? '');
  const methodRaw = String(pay.paymentMethod ?? pay.method ?? 'cash');
  return {
    id: String(pay.id),
    invoiceId: invId,
    method: methodRaw,
    amount: amt,
    date: String(pay.created_at ?? pay.createdAt ?? new Date().toISOString()),
    reference:
      (pay.provider_payment_id as string | undefined) ??
      (pay.transactionId as string | undefined) ??
      (pay.reference as string | undefined),
    notes: String(pay.notes ?? ''),
    createdAt: String(pay.created_at ?? pay.createdAt ?? new Date().toISOString()),
    provider: (pay.provider as string | null) ?? null,
    providerStatus: st || null
  };
}

interface InvoiceItem {
  kind: 'service' | 'product';
  refId: string;
  name: string;
  qty: number;
  price: number;
  taxRate: number;
  total: number;
}

interface Invoice {
  id: string;
  clinicId: string;
  clientId: string;
  clientName: string;
  number: string;
  series: string;
  date: string;
  items: InvoiceItem[];
  totals: {
    net: number;
    vat: number;
    gross: number;
  };
  /** status din API (due, partially_paid, paid, void, …) */
  apiStatus: string;
  issuedAt?: string;
  paidAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface Payment {
  id: string;
  invoiceId: string;
  method: string;
  amount: number;
  date: string;
  reference?: string;
  notes?: string;
  createdAt: string;
  /** stripe | null — din API */
  provider?: string | null;
  /** status brut API (completed, succeeded, failed, …) */
  providerStatus?: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BillingManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedMethod, setSelectedMethod] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reconcileInfo, setReconcileInfo] = useState<string | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);

  const loadBilling = React.useCallback(async () => {
    if (!user?.clinicId) return;

    setLoading(true);
    setBillingError(null);

    try {
      const [invList, paymentsResponse] = await Promise.all([
        invoicesService.list({ page: 1, pageSize: 500 }),
        paymentsService.getPayments({
          clinicId: user.clinicId,
          page: 1,
          limit: 500
        })
      ]);

      const items = (invList as { items?: unknown[] })?.items ?? [];
      const transformedInvoices: Invoice[] = (items as Record<string, unknown>[]).map((row) => {
        const amountCents = Number(row.amountCents ?? row.amount_cents ?? 0) || 0;
        const paidCents = Number(row.paidAmountCents ?? row.paid_amount_cents ?? 0) || 0;
        const gross = amountCents / 100;
        const net = gross / 1.19;
        const vat = gross - net;
        const client = row.client as { firstName?: string; lastName?: string } | undefined;
        const clientName = client
          ? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || '—'
          : '—';
        const st = String(row.status ?? 'draft');
        return {
          id: String(row.id),
          clinicId: user.clinicId!,
          clientId: String(row.clientId ?? row.client_id ?? ''),
          clientName,
          number: String(row.number ?? '—'),
          series: '',
          date: String(row.issueDate ?? row.issue_date ?? row.createdAt ?? new Date().toISOString()),
          items: [],
          totals: { net, vat, gross },
          apiStatus: st,
          issuedAt: String(row.issueDate ?? row.issue_date ?? ''),
          paidAt: st === 'paid' ? String(row.issueDate ?? '') : undefined,
          notes: '',
          createdAt: String(row.createdAt ?? row.created_at ?? new Date().toISOString()),
          updatedAt: String(row.createdAt ?? row.created_at ?? new Date().toISOString())
        };
      });

      setInvoices(transformedInvoices);

      const paymentsArray = (paymentsResponse.payments || []) as unknown as Record<string, unknown>[];
      const transformedPayments: Payment[] = paymentsArray.map((pay) =>
        normalizePaymentRow(pay, user.clinicId!)
      );
      setPayments(transformedPayments);
    } catch (error) {
      console.error('Eroare la incarcarea datelor de facturare:', error);
      setBillingError('Nu s-au putut încărca facturile sau plățile.');
      setInvoices([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [user?.clinicId]);

  useEffect(() => {
    void loadBilling();
  }, [loadBilling]);

  const getInvoiceStatusColor = (status: string) => {
    switch (String(status).toLowerCase()) {
      case 'draft':
        return 'default';
      case 'due':
      case 'overdue':
      case 'partially_paid':
      case 'unpaid':
        return 'warning';
      case 'paid':
        return 'success';
      case 'void':
      case 'cancelled':
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getInvoiceStatusLabel = (status: string) => {
    switch (String(status).toLowerCase()) {
      case 'draft':
        return 'Draft';
      case 'due':
        return 'De plată';
      case 'overdue':
        return 'Restantă';
      case 'partially_paid':
        return 'Plată parțială';
      case 'unpaid':
        return 'Neplătită';
      case 'paid':
        return 'Plătită';
      case 'void':
      case 'cancelled':
      case 'canceled':
        return 'Anulată';
      default:
        return status;
    }
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'success';
      case 'card':
        return 'info';
      case 'transfer':
        return 'primary';
      case 'voucher':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Cash';
      case 'card':
        return 'Card';
      case 'transfer':
        return 'Transfer';
      case 'voucher':
        return 'Voucher';
      default:
        return method;
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return <LocalAtmIcon />;
      case 'card':
        return <CreditCardIcon />;
      case 'transfer':
        return <AccountBalanceIcon />;
      case 'voucher':
        return <ReceiptIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDownloadPdf = async (invoiceId: string) => {
    setBillingError(null);
    try {
      const blob = await invoicesService.getPdfBlob(invoiceId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${invoiceId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setBillingError('Nu s-a putut descărca PDF-ul facturii.');
    }
  };

  const handleIssueInvoice = async (invoiceId: string) => {
    setBillingError(null);
    try {
      await invoicesService.issue(invoiceId);
      await loadBilling();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setBillingError(ax?.response?.data?.error || 'Emiterea facturii a eșuat.');
    }
  };

  const handleStornoInvoice = async (invoiceId: string) => {
    const reason = window.prompt('Motiv stornare (opțional):') ?? undefined;
    setBillingError(null);
    try {
      await invoicesService.storno(invoiceId, reason);
      await loadBilling();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setBillingError(ax?.response?.data?.error || 'Stornarea a eșuat.');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.series.toLowerCase().includes(searchTerm.toLowerCase());

    const bucket = invoiceTabBucket(invoice.apiStatus);
    const matchesStatus =
      selectedStatus === 'all' ||
      (selectedStatus === 'draft' && bucket === 'draft') ||
      (selectedStatus === 'open' && bucket === 'open') ||
      (selectedStatus === 'paid' && bucket === 'paid') ||
      (selectedStatus === 'cancelled' && bucket === 'cancelled');

    return matchesSearch && matchesStatus;
  });

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesMethod = selectedMethod === 'all' || payment.method === selectedMethod;
    
    return matchesSearch && matchesMethod;
  });

  const draftInvoices = filteredInvoices.filter((i) => invoiceTabBucket(i.apiStatus) === 'draft');
  const openInvoices = filteredInvoices.filter((i) => invoiceTabBucket(i.apiStatus) === 'open');
  const paidInvoices = filteredInvoices.filter((i) => invoiceTabBucket(i.apiStatus) === 'paid');
  const allInvoices = filteredInvoices;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca facturile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {billingError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBillingError(null)}>
          {billingError}
        </Alert>
      )}
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Facturare & Plati
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Documente fiscale si incasari - proforme, facturi, chitante, split plati, storno, export contabilitate
          </Typography>
        </Box>
        <Button
          variant="contained"
          component={RouterLink}
          to="/clinic/visits"
          startIcon={<AddIcon />}
          sx={{ borderRadius: 2 }}
        >
          Facturare din vizită
        </Button>
      </Box>

      {/* Statistici */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  <ReceiptIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {allInvoices.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Facturi Totale
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.light' }}>
                  <ScheduleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {openInvoices.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Neplatite
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.light' }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {paidInvoices.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Platite
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.light' }}>
                  <MoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {allInvoices.reduce((sum, invoice) => sum + invoice.totals.gross, 0).toFixed(0)} RON
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valoare Totala
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Cauta factura, client sau serie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="open">De plată / restant</MenuItem>
                  <MenuItem value="paid">Platita</MenuItem>
                  <MenuItem value="cancelled">Anulata</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Metoda Plata</InputLabel>
                <Select
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                  label="Metoda Plata"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="transfer">Transfer</MenuItem>
                  <MenuItem value="voucher">Voucher</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Tooltip title="Actualizează lista">
                <IconButton onClick={() => void loadBilling()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="billing tabs">
            <Tab 
              label={
                <Badge badgeContent={draftInvoices.length} color="default">
                  Draft
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={openInvoices.length} color="warning">
                  Neplatite
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={paidInvoices.length} color="success">
                  Platite
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={filteredPayments.length} color="info">
                  Plati
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={allInvoices.length} color="primary">
                  Toate
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Factura</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Produse/Servicii</TableCell>
                  <TableCell>Valoare</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {draftInvoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'default.light' }}>
                            <ReceiptIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {invoice.series} {invoice.number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateOnly(invoice.date)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateOnly(invoice.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.items.length} iteme
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.items.map(item => item.name).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {invoice.totals.gross} RON
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          TVA: {invoice.totals.vat} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getInvoiceStatusLabel(invoice.apiStatus)}
                          color={getInvoiceStatusColor(invoice.apiStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Descarcă PDF">
                            <IconButton size="small" onClick={() => void handleDownloadPdf(invoice.id)}>
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Emite factura (status: de plată)">
                            <IconButton size="small" onClick={() => void handleIssueInvoice(invoice.id)}>
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={draftInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Factura</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Produse/Servicii</TableCell>
                  <TableCell>Valoare</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {openInvoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.light' }}>
                            <ReceiptIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {invoice.series} {invoice.number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateOnly(invoice.date)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateOnly(invoice.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.items.length} iteme
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.items.map(item => item.name).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {invoice.totals.gross} RON
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          TVA: {invoice.totals.vat} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getInvoiceStatusLabel(invoice.apiStatus)}
                          color={getInvoiceStatusColor(invoice.apiStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Descarcă PDF">
                            <IconButton size="small" onClick={() => void handleDownloadPdf(invoice.id)}>
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={openInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Factura</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Produse/Servicii</TableCell>
                  <TableCell>Valoare</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paidInvoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'success.light' }}>
                            <ReceiptIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {invoice.series} {invoice.number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateOnly(invoice.date)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateOnly(invoice.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.items.length} iteme
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.items.map(item => item.name).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {invoice.totals.gross} RON
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          TVA: {invoice.totals.vat} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getInvoiceStatusLabel(invoice.apiStatus)}
                          color={getInvoiceStatusColor(invoice.apiStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Descarcă PDF">
                            <IconButton size="small" onClick={() => void handleDownloadPdf(invoice.id)}>
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Stornare administrativă">
                            <IconButton size="small" onClick={() => void handleStornoInvoice(invoice.id)}>
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={paidInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {reconcileInfo && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setReconcileInfo(null)}>
              {reconcileInfo}
            </Alert>
          )}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={async () => {
                try {
                  const r = await clinicPaymentsService.reconcile({ apply_safe_fixes: false });
                  setReconcileInfo(
                    `Verificate ${r.checked}; remedieri sigure aplicate: ${r.updated}; diferențe raportate: ${r.mismatches.length}`
                  );
                } catch (e: unknown) {
                  const ax = e as { response?: { data?: { error?: string } } };
                  setReconcileInfo(ax?.response?.data?.error || 'Eroare reconciliere');
                }
              }}
            >
              Raport reconciliere Stripe
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plata</TableCell>
                  <TableCell>Factura</TableCell>
                  <TableCell>Metoda</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Suma</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Referinta</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'info.light' }}>
                            {getMethodIcon(payment.method)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {payment.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateOnly(payment.date)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {payment.invoiceId
                            ? `${invoices.find((i) => i.id === payment.invoiceId)?.series ?? ''} ${invoices.find((i) => i.id === payment.invoiceId)?.number ?? ''}`.trim() ||
                              payment.invoiceId.slice(0, 8)
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getMethodLabel(payment.method)}
                          color={getMethodColor(payment.method) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={payment.provider === 'stripe' ? String(payment.providerStatus || '—') : String(payment.providerStatus || '—')}
                          color={payment.provider === 'stripe' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {payment.amount} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateOnly(payment.date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeOnly(payment.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {payment.reference || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          {payment.provider === 'stripe' && canStripeRetry(payment.providerStatus) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={async () => {
                                try {
                                  await clinicPaymentsService.retryPayment(payment.id);
                                  setReconcileInfo(
                                    'S-a creat un PaymentIntent nou. Finalizați plata din panoul de vizită sau fluxul de încasare Stripe.'
                                  );
                                  await loadBilling();
                                } catch (e: unknown) {
                                  const ax = e as { response?: { data?: { error?: string } } };
                                  setReconcileInfo(ax?.response?.data?.error || 'Eroare retry');
                                }
                              }}
                            >
                              Retry
                            </Button>
                          )}
                          {payment.provider === 'stripe' && isStripeChargeSuccessful(payment.providerStatus) && (
                            <Button
                              size="small"
                              color="warning"
                              variant="outlined"
                              onClick={async () => {
                                const raw = window.prompt('Sumă rambursare (RON); gol = integral:', '');
                                try {
                                  await clinicPaymentsService.refund(
                                    payment.id,
                                    raw && raw.trim() !== '' ? parseFloat(raw) : undefined
                                  );
                                  await loadBilling();
                                } catch (e: unknown) {
                                  const ax = e as { response?: { data?: { error?: string } } };
                                  setReconcileInfo(ax?.response?.data?.error || 'Eroare refund');
                                }
                              }}
                            >
                              Refund
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPayments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Factura</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Produse/Servicii</TableCell>
                  <TableCell>Valoare</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allInvoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor:
                                invoiceTabBucket(invoice.apiStatus) === 'paid'
                                  ? 'success.light'
                                  : invoiceTabBucket(invoice.apiStatus) === 'open'
                                    ? 'warning.light'
                                    : 'default.light'
                            }}
                          >
                            <ReceiptIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {invoice.series} {invoice.number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateOnly(invoice.date)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateOnly(invoice.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.items.length} iteme
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.items.map(item => item.name).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {invoice.totals.gross} RON
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          TVA: {invoice.totals.vat} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getInvoiceStatusLabel(invoice.apiStatus)}
                          color={getInvoiceStatusColor(invoice.apiStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Descarcă PDF">
                            <IconButton size="small" onClick={() => void handleDownloadPdf(invoice.id)}>
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          {invoiceTabBucket(invoice.apiStatus) === 'draft' && (
                            <Tooltip title="Emite factura (status: de plată)">
                              <IconButton size="small" onClick={() => void handleIssueInvoice(invoice.id)}>
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {invoiceTabBucket(invoice.apiStatus) === 'paid' && (
                            <Tooltip title="Stornare administrativă">
                              <IconButton size="small" onClick={() => void handleStornoInvoice(invoice.id)}>
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={allInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>
      </Card>
    </Box>
  );
};

export default BillingManagement;

